/**
 * Microsoft Clarity (heatmaps + session recordings) — consent-gated loader.
 *
 * Clarity writes first-party cookies (_clck/_clsk) and records what the visitor
 * sees and does, so it rides the same gate as GA4: the tag is never injected
 * until the visitor accepts the "analytics" category in the cookie banner, and
 * super-admin browsers never load it at all. Loading from here instead of
 * index.html is what makes "nothing before consent" literally true for
 * first-time visitors — CookieConsent's syncConsent() is the only caller.
 *
 * Clarity enforces its own Consent Mode for EEA/UK/CH traffic, so once the tag
 * is up we still hand it an explicit consentv2 signal; without one it stays in
 * no-consent mode and can't stitch a session across page views. Ad storage is
 * always denied — the banner has no marketing category that could grant it.
 */

/** Clarity project id — Settings → Setup in clarity.microsoft.com. */
export const CLARITY_PROJECT_ID = "y0lteuiskt";

/**
 * The loader stub, not the recorder: it pulls scripts.clarity.ms/<ver>/clarity.js,
 * pings c.clarity.ms/c.gif and uploads to y.clarity.ms — which is why the CSP in
 * vercel.json allows https://*.clarity.ms rather than just www.
 */
const TAG_SRC = `https://www.clarity.ms/tag/${CLARITY_PROJECT_ID}`;

type ClarityFn = ((...args: unknown[]) => void) & { q?: unknown[][] };

declare global {
  interface Window {
    clarity?: ClarityFn;
  }
}

let tagEl: HTMLScriptElement | null = null;
let state: "idle" | "running" | "stopped" = "idle";

/**
 * Microsoft's command queue, behaviourally identical to their inline snippet.
 * The tag script calls window.clarity(...) itself before the recorder has
 * downloaded, so the stub must exist BEFORE the tag element is appended.
 */
function ensureQueue(): ClarityFn {
  if (!window.clarity) {
    const stub: ClarityFn = (...args: unknown[]) => {
      (stub.q = stub.q || []).push(args);
    };
    window.clarity = stub;
  }
  return window.clarity;
}

function injectTag(): void {
  tagEl?.remove();
  const el = document.createElement("script");
  el.async = true;
  el.src = TAG_SRC;
  document.head.appendChild(el);
  tagEl = el;
}

/** Load Clarity (idempotent) and grant analytics storage. */
export function enableClarity(): void {
  if (typeof window === "undefined" || typeof document === "undefined") return;
  const clarity = ensureQueue();
  // "stopped" means consent was withdrawn earlier in this page load: the
  // recorder shut itself down and only a fresh "start" revives it — and that
  // start has to come from the tag, which carries project config (upload host,
  // cookie list, expiry) we must not hardcode here.
  if (state !== "running") injectTag();
  clarity("consentv2", { ad_Storage: "denied", analytics_Storage: "granted" });
  state = "running";
}

/**
 * Withdraw consent: tell Clarity to drop its cookies and end the session, then
 * halt collection for the rest of this page load. Re-granting re-injects the
 * tag (see enableClarity).
 */
export function disableClarity(): void {
  if (typeof window === "undefined") return;
  const clarity = window.clarity;
  if (state !== "running" || typeof clarity !== "function") return;
  // Same handler as the documented clarity('consent', false): deletes
  // _clck/_clsk and restarts in no-consent mode.
  clarity("consentv2", { ad_Storage: "denied", analytics_Storage: "denied" });
  // Must come last — after "stop" the API degrades to a queue that only
  // responds to "start", so any later command would just sit unread.
  clarity("stop");
  state = "stopped";
}
